/** @jsxImportSource @emotion/react */

import { msg } from '@lingui/core/macro';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { BoardPresets, boardPresets } from 'modules/template-editor/domain/consts/form/boards/boardPresets';
import { getTypeOptions } from 'modules/template-editor/ui/components/elements/components/events-selector/type';
import BoardForm from 'modules/template-editor/ui/components/elements/forms/board-form';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppDispatch, history } from 'store/store';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { Values } from 'modules/template-editor/domain/interface/form/board';
import ValueTypeValidator from 'modules/template-editor/domain/utils/form/board/value-type-validator';
import { createBoard } from 'modules/template-editor/store/templates/actions';
import { DEFAULT_BOARD_DURATION } from 'modules/template-editor/domain/template-file';
import { resetPressedState, setActiveBoard } from 'modules/template-editor/store/templates/templates';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import { useActiveBoardTemplate } from 'modules/template-editor/ui/hooks/use-active-board-template';

const PRESET = BoardPresets.create;

function useCreateModal() {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const { info } = useOrganizer();
  const { boards, isProcessed, isFinished, process } = useBoards();
  const activeTemplate = useActiveBoardTemplate();
  const location = useLocation();
  const isCatalog = location.pathname.includes('catalog');

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    // `async` + промис запроса — Formik держит кнопку формы запертой до ответа (IMGM-1561).
    async (values: Values) => {
      if (!info) {
        console.warn({
          event: 'failed to create category: info is undefined',
          info,
        });
        return;
      }

      if (!activeTemplate) {
        console.warn({
          event: 'failed to create category: activeTemplate is undefined',
          activeTemplate,
        });
        return;
      }

      const { name, duration, objects, version, type, events } = ValueTypeValidator({ values });
      const isEvents = type === 'events';
      const isCarousel = type === 'carousel';
      const maxOrder = boards.at(-1)?.order || 0;

      return dispatchWithToast(
        createBoard({
          templateId: activeTemplate.id,
          name: name.trim(),
          version,
          objects,
          type,
          duration: isEvents || isCarousel ? duration : undefined,
          events: isEvents ? events : undefined,
          order: maxOrder + 1,
        }),
        { error: msg`Не удалось создать табло` },
      );
    },
    [activeTemplate, boards, dispatchWithToast, info],
  );

  const initialValue: Values = {
    name: '',
    type: getTypeOptions().find((type) => type.value === 'static'),
    duration: DEFAULT_BOARD_DURATION,
    objects: [],
    version: '5.3.0',
    events: [],
  };

  useEffect(() => {
    if (!isFinished) {
      return;
    }
    if (process !== 'create board') {
      return;
    }
    close();
    dispatch(resetPressedState());
    const lastBoard = boards.at(-1);
    if (!lastBoard) {
      throw Error('The last created board was not found');
    }

    dispatch(setActiveBoard({ id: lastBoard.id }));

    if (activeTemplate?.id) {
      history.push(Pages.TEMPLATE.builders.editor(activeTemplate?.id, lastBoard.id), { from: isCatalog ? 'catalog' : 'boards' });
    } else {
      console.error({
        event: 'go to editor',
        message: 'activeTemplate is undefined',
        activeTemplate,
        lastBoard,
      });
      toast.warn(t`Не удалось перейти к редактированию табло`);
    }
  }, [activeTemplate, activeTemplate?.id, boards, close, dispatch, isCatalog, isFinished, process, t]);

  const existingNames = useMemo(() => boards.map((board) => board.name.trim()), [boards]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close} noOverflow size="md">
        <UDText type={'title-3'} className="mb-5">
          {t(boardPresets[PRESET].header)}
        </UDText>
        <BoardForm
          onSubmit={onSubmit}
          initialValues={initialValue}
          isLoading={isProcessed}
          preset={PRESET}
          existingNames={existingNames}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useCreateModal;
